import { useToast } from '@/components/ui/use-toast';
import { useAddTagToPlotcard, useMultiplePlotcardTags, useProjectTags, useRemoveTagFromPlotcard } from '@/services/query/tags-queries';
import { Tag } from '@/services/supabase/tags';
import { useEffect, useState } from 'react';

// UI Components
import { MultiSelect } from '@/components/ui/multi-select';

interface TagSelectorProps {
  projectId: string | number;
  plotcardId: number;
  onTagsChange?: (tags: Tag[]) => void;
  className?: string;
}

export function TagSelector({ projectId, plotcardId, onTagsChange, className = '' }: TagSelectorProps) {
  const { toast } = useToast();
  // Queries
  const { data: projectTags = [] } = useProjectTags(projectId);

  // Use the batch tag fetching approach
  const { data: tagsMap = {}, isLoading: isLoadingPlotcardTags } = useMultiplePlotcardTags([plotcardId]);
  // Extract tags for this plotcard from the map
  // Extract tags for this plotcard from the map
  const serverTags = tagsMap[plotcardId] || [];

  // Local optimistic state
  const [localTags, setLocalTags] = useState<Tag[]>([]);

  // Sync local state with server data when it changes
  useEffect(() => {
    if (serverTags) {
      setLocalTags(serverTags);
    }
  }, [serverTags]);

  // Mutations
  const addTagMutation = useAddTagToPlotcard();
  const removeTagMutation = useRemoveTagFromPlotcard();

  const handleTagsChange = async (newSelectedIds: (string | number)[]) => {
    const currentIds = localTags.map(t => t.id);

    // Find added ID
    const addedId = newSelectedIds.find(id => !currentIds.includes(id as number));
    // Find removed ID
    const removedId = currentIds.find(id => !newSelectedIds.includes(id));

    if (addedId) {
      // Add Logic
      const tagToAdd = projectTags.find(t => t.id === addedId);
      if (!tagToAdd) return;

      // Optimistic update
      const newTags = [...localTags, tagToAdd];
      setLocalTags(newTags);
      if (onTagsChange) onTagsChange(newTags);

      try {
        await addTagMutation.mutateAsync({ plotcardId, tagId: addedId as number });
      } catch {
        // Revert
        setLocalTags(localTags);
        if (onTagsChange) onTagsChange(localTags);
        toast({ title: "Error", description: "Failed to add tag", variant: "destructive" });
      }
    }

    if (removedId) {
      // Remove Logic
      // Optimistic update
      const newTags = localTags.filter(t => t.id !== removedId);
      setLocalTags(newTags);
      if (onTagsChange) onTagsChange(newTags);

      try {
        await removeTagMutation.mutateAsync({ plotcardId, tagId: removedId });
      } catch {
        // Revert
        setLocalTags(localTags);
        if (onTagsChange) onTagsChange(localTags);
        toast({ title: "Error", description: "Failed to remove tag", variant: "destructive" });
      }
    }
  };

  return (
    <div className={className}>
      <MultiSelect
        options={projectTags.map(tag => ({ label: tag.name, value: tag.id }))}
        selected={localTags.map(tag => tag.id)}
        onChange={handleTagsChange}
        placeholder="Search and select tags..."
        emptyText="No tags found"
        isLoading={isLoadingPlotcardTags}
        className="min-h-11 rounded-xl border-muted-foreground/10 bg-muted/30 focus-within:ring-primary/20 hover:bg-muted/40 transition-colors shadow-sm"
        badgeClassName="rounded-lg bg-background/50 hover:bg-background/80 border border-border/10"
      />
    </div>
  );
}
